import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { CommandBar, ICommandBarItemProps, ICommandBarStyles, mergeStyles } from "@fluentui/react";
import { useAsync } from "@fluentui/react-hooks";
import { css, useTheme } from "cayo.ui";
import styled from "styled-components";
import { PropertyAnnotation } from "@cayo/shared/api/types/cayo-graph";
import { IColumn } from "@cayo/shared/api/types/schema";
import { isConditionsEqual } from "@cayo/shared/components/GridContainer/gridModel/Query";
import { resetToDefault } from "@cayo/shared/components/GridContainer/gridModel/utils";
import {
  GridStateQueryMode,
  IQuery,
  SavedQueryItem,
  DispatchAction,
} from "@cayo/shared/components/GridContainer/types";
import logger from "@cayo/shared/libs/logger";
import { objectUtils } from "@cayo/shared/utils/object";
import { IObjectPicker, IQuickFilterBar } from "../../api/schema.api";
import AnrSearchBox from "../AnrSearchBox";
import { ApplyFilterButton } from "./GridFilterBar.ApplyFilterButton";
import { ManageQuery } from "./GridFilterBar.ManageQuery";
import { pickerValueMap } from "./GridFilterBar.utils";
import { useSchemes } from "@cayo/shared/components/App/hooks";
import { commandBarMessages } from "../CommandBar/messages";
import { InplaceConditionBuilder } from "../ConditionBuilder/ConditionBuilder";
import { RestrictionBanner } from "../Freemium";
import useTypeAnnotations from "../GlobalHooks/type-annotations.hook";
import renderComponent from "../QuickFilterBar/renderers";
import { useGridContextMenu } from "./hooks/useGridContextMenu";

const log = logger.getLogger("QUICK-FILTER-NEW GridFilterBar");

export type GroupConfig = {
  key: string;
  text: string;
};

type HybridQueryType = {
  advancedFilter: string;
  mainQueryName: string;
  mainQueryId: string;
};

type GridFilterBarProps = {
  schema: IQuickFilterBar;
  columns?: IColumn[];
  propertyAnnotations: PropertyAnnotation[] | undefined;
  dispatch: DispatchAction;
  query: IQuery;
  savedQueries: Array<SavedQueryItem>;
  recentQueries: Array<string>;
  favoriteQueries?: Array<string>;
  groupConfig?: GroupConfig[];
  targetType?: string;
  isSearchSupported?: boolean;
  readonly?: boolean;
  restrictedFilter?: boolean;
  gridSettingsKey: string;
  queryMode: GridStateQueryMode;
  queryUrl: string;
  entitySetPath: string;
  previewQueryId: string | null;
  selectedItems: Array<any>;
  setAbortControllerState: (props: AbortController | null) => void;
  showSearchButton: boolean;
};

export function GridFilterBar(props: GridFilterBarProps): JSX.Element {
  const intl = useIntl();
  const theme = useTheme();
  const async = useAsync();
  const brandColors = theme.cayoTheme.brandColors;
  const { bodyDivider } = theme.officeTheme.semanticColors;
  const { getIconAnnotation, getTypeAnnotation } = useTypeAnnotations();
  const pickerAnnotations = props.schema.annotations ?? [];
  const [innerQuery, setInnerQuery] = useState<IQuery>(() => ({ conditions: [], isSystem: false }));
  const [isDirty, setIsDirty] = useState(false);
  const [isPickerOpened, setIsPickerOpened] = useState(false);
  const [pickers, setPickers] = useState<ICommandBarItemProps[]>([]);
  const { clientComponents, removeClientComponent } = useSchemes();

  const selectedItemObjectName = props.selectedItems.length
    ? props.selectedItems[0].targetObjectName
    : "";
  const selectedItemId = props.selectedItems.length ? props.selectedItems[0].id : "";

  const { query, dispatch, groupConfig, previewQueryId, showSearchButton } = props;
  const allowClear = !props.query.id
    ? !!props.query.isTemp
    : !!props.query.isTemp || !props.query.isDefault;

  useEffect(() => {
    log.debug(".mount -> props, query", props, innerQuery);
  }, []);

  useEffect(
    function initInnerQuery() {
      setInnerQuery(objectUtils.cloneObject(props.query));
      setIsDirty(false);
    },
    [props.query]
  );

  const apply = useCallback(() => {
    async.setTimeout(() => {
      setIsPickerOpened(false);
      setIsDirty(false);
      props.dispatch({
        kind: "changeQuery",
        query: innerQuery,
        previewQuery: !!props.previewQueryId,
      });
    }, 20);
  }, [props.dispatch, innerQuery, props.previewQueryId]);

  const applyHybridQuery = ({ advancedFilter, mainQueryName, mainQueryId }: HybridQueryType) => {
    let resultQuery = pickerValueMap.toQuery(
      advancedFilter,
      { name: "advancedFilter" } as any,
      innerQuery
    );

    resultQuery = resetToDefault(resultQuery, mainQueryId, mainQueryName);

    props.dispatch({
      kind: "changeQuery",
      query: resultQuery,
      previewQuery: !!props.previewQueryId,
    });
  };

  const HybridSearchModal = useMemo(() => {
    if (!clientComponents.length) {
      return null;
    }
    const Component = clientComponents.find(
      (c) => c.parameters?.id === "HybridSearchModal"
    )?.component;

    if (!Component) {
      return null;
    }

    return (
      <Component
        selectedItemId={selectedItemId}
        selectedItemObjectName={selectedItemObjectName}
        savedQueries={props.savedQueries}
        entitySetPath={props.entitySetPath}
        applyHybridQuery={applyHybridQuery}
        onClose={removeClientComponent}
      />
    );
  }, [
    clientComponents,
    selectedItemObjectName,
    selectedItemId,
    props.savedQueries,
    props.entitySetPath,
    applyHybridQuery,
    removeClientComponent,
  ]);

  const AiAssistantModal = useMemo(() => {
    if (!clientComponents.length) {
      return null;
    }
    const Component = clientComponents.find(
      (c) => c.parameters?.id === "AiAssistantModal"
    )?.component;

    if (!Component) {
      return null;
    }

    return (
      <Component
        savedQueries={props.savedQueries}
        entitySetPath={props.entitySetPath}
        applyHybridQuery={applyHybridQuery}
        onClose={removeClientComponent}
      />
    );
  }, [
    clientComponents,
    selectedItemObjectName,
    selectedItemId,
    props.savedQueries,
    props.entitySetPath,
    applyHybridQuery,
    removeClientComponent,
  ]);

  const cacheKeyHash = useMemo(() => Date.now().toString(), [innerQuery]);

  const { gridContextMenu, gridContextMenuStyle } = useGridContextMenu({
    dispatch,
    query,
    allowClear,
    groupConfig,
    previewQueryId,
  });

  const currentQuery: ICommandBarItemProps = {
    key: "currentQuery",
    cacheKey: `currentQuery-${props.query.name}`,
    text: props.query.name || intl.formatMessage(commandBarMessages.filter),
    iconProps: { iconName: resolveIcon(props.queryMode) },
    buttonStyles: gridContextMenuStyle,
    onClick: () => {
      props.dispatch({ kind: "openQueryPicker" });
    },
  };

  const manageQuery: ICommandBarItemProps = useMemo(() => {
    return {
      key: "manageQuery",
      cacheKey: `manageQuery-${cacheKeyHash}`,
      onRender: () => (
        <ManageQuery
          key="mq"
          currentQuery={props.query}
          dispatch={props.dispatch}
          // makeForEdit={makeForEdit}
        ></ManageQuery>
      ),
    };
  }, [cacheKeyHash, props.schema, props.query, props.dispatch]);

  const anrFilter: ICommandBarItemProps = useMemo(() => {
    return {
      key: "search-box",

      onRender: () => (
        <div className={css.px8}>
          <AnrSearchBox
            key="anr-sb"
            width={200}
            query={innerQuery}
            dispatch={props.dispatch}
            showSearchButton={showSearchButton}
          />
        </div>
      ),
    };
  }, [innerQuery, props.dispatch]);

  useEffect(() => {
    function generatePickers() {
      const result = [];

      for (const annotation of pickerAnnotations as Array<IObjectPicker>) {
        let pickerItem;

        if (annotation.columns && annotation.columns.length === 0) {
          annotation.columns = undefined;
        }

        if (annotation.defaultOrder && annotation.defaultOrder.length === 0) {
          annotation.defaultOrder = undefined;
        }

        if (
          annotation.name === "advancedFilter" &&
          import.meta.env.REACT_APP_ENABLE_CONDITION_BUILDER === "true"
        ) {
          pickerItem = {
            key: `picker-condition-builder`,
            cacheKey: `picker-condition-builder-${cacheKeyHash}`,
            onRender: () => (
              <div className={pickerContainerClass}>
                <InplaceConditionBuilder
                  query={props.query}
                  dispatch={props.dispatch}
                  columns={props.columns}
                  objectTypesUrl={props.schema["typeInfoPath"]}
                  objectTypesOptionsUrl={props.schema["typeDetailsPath"]}
                  filterAnnotations={props.schema.annotations}
                  onPickerStateChanged={(isOpened) => {
                    log.debug(
                      "picker.onPickerStateChanged -> name, isOpened",
                      annotation.name,
                      isOpened
                    );
                    setIsPickerOpened(isOpened);
                  }}
                />
              </div>
            ),
          };
        } else {
          const possibleValues =
            props.propertyAnnotations?.find(
              (p) => p.target === `${props.targetType}/${annotation.name}`
            )?.possibleValues ?? [];

          const value = pickerValueMap.fromConditions(
            innerQuery.conditions,
            annotation,
            possibleValues
          );
          const title = (annotation as unknown as IObjectPicker).title;
          log.debug(`renderComponent "${annotation.name ?? title}" -> args"`, {
            value,
            innerQuery,
            annotation,
            possibleValues,
          });

          pickerItem = {
            key: `picker-${annotation.name ?? title}`,
            cacheKey: `picker-${annotation.name ?? title}-${cacheKeyHash}`,
            onRender: () => (
              <div className={pickerContainerClass}>
                {renderComponent(
                  annotation,
                  value,
                  (v, annotation, expression) => {
                    const resultQuery = pickerValueMap.toQuery(
                      v,
                      annotation as any /*TODO: remove cast*/,
                      innerQuery,
                      expression
                    );
                    // if (annotation.name === "advancedFilter") debugger;
                    log.debug(`picker.setValue "${annotation.name ?? title}"-> args"`, {
                      value: v,
                      annotation,
                      expression,
                      resultQuery: resultQuery,
                      innerQuery,
                      currentQuery: props.query,
                    });

                    if ((annotation as IObjectPicker).type === "toggle") {
                      props.dispatch({
                        kind: "changeQuery",
                        query: resultQuery,
                        previewQuery: !!props.previewQueryId,
                      });
                    } else {
                      setIsDirty(
                        !isConditionsEqual(props.query.conditions, resultQuery.conditions)
                      );
                      setInnerQuery(resultQuery);
                    }
                  },
                  () => {},
                  {
                    intl,
                    getIconAnnotation,
                    getTypeAnnotation,
                    theme,
                    onApply: () => {
                      apply();
                    },
                    onPickerStateChanged: (isOpened) => {
                      log.debug(
                        "picker.onPickerStateChanged -> name, isOpened",
                        annotation.name,
                        isOpened
                      );
                      setIsPickerOpened(isOpened);
                    },
                    targetType: props.schema.targetType || props.targetType,
                    propertyAnnotations: props.propertyAnnotations,
                    possibleValues,
                    innerQuery,
                    queryUrl: props.queryUrl,
                  }
                )}
              </div>
            ),
          };
        }
        result.push(pickerItem);
      }

      setPickers(result);
    }

    generatePickers();
  }, [
    cacheKeyHash,
    pickerAnnotations,
    props.schema,
    props.propertyAnnotations,
    innerQuery,
    props.queryUrl,
    props.previewQueryId,
    apply,
    setIsDirty,
    setInnerQuery,
    setIsPickerOpened,
  ]);

  const farItems: ICommandBarItemProps[] = [gridContextMenu];

  const getItems = () => {
    const result = [];
    if (props.schema.showSavedQueries) {
      // result.push(queryList);
      result.push(currentQuery);
      if (!props.readonly) {
        result.push(manageQuery);
      }
    }
    if (props.isSearchSupported) {
      result.push(anrFilter);
    }
    return result.concat(pickers);
  };

  const commandBarItems: ICommandBarItemProps[] = getItems();

  const commandBarStyles: ICommandBarStyles = useMemo(
    () => ({
      root: {
        paddingLeft: 0,
        paddingRight: 0,
        background: bodyDivider,
        borderRadius: 4,
        height: 40,

        selectors: {
          "& > .ms-CommandBar-primaryCommand": { alignItems: "center" },
          ":after": {
            position: "absolute",
            height: 1,
            left: 5,
            right: 5,
            bottom: -8,
            backgroundColor: brandColors.divider,
            content: "''",
          },
        },
      },
    }),
    [brandColors]
  );

  return (
    <>
      <StyledCommandBar
        items={commandBarItems}
        farItems={farItems}
        styles={commandBarStyles}
        overflowButtonProps={{ styles: gridContextMenuStyle, ariaLabel: "More commands" }}
      />
      {isDirty && !isPickerOpened && props?.restrictedFilter && <RestrictionBanner />}
      {isDirty && !isPickerOpened && <ApplyFilterButton apply={apply} />}
      {HybridSearchModal}
      {AiAssistantModal}
    </>
  );
}

const StyledCommandBar = styled(CommandBar)`
  min-width: 100%;
  width: 1px;
  align-self: stretch;
`;

const pickerContainerClass = mergeStyles({
  maxWidth: 200,
  selectors: {
    ".ms-Button-textContainer": {
      width: "calc(100% - 20px)",
    },
  },
});

function resolveIcon(mode: GridStateQueryMode) {
  switch (mode) {
    case "normal":
      return "FilterSolid";
    case "preview":
      return "RedEye";
  }
}
